import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Sidebar from "../../components/Sidebar";
import { AppHeader } from "../../components/layout/AppHeader";
import { AppointmentAPI } from "../../services/appointment.service";
import type {
  AppointmentSlot,
  BulkRescheduleResponse,
  BulkRescheduleSource,
  SuggestedSlotItem,
} from "../../services/appointment.service";

const fmt = (iso: string) =>
  new Date(iso).toLocaleString("en-IN", {
    weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });

const fmtTimeOnly = (iso: string) =>
  new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

const fmtDayLabel = (iso: string) =>
  new Date(iso).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });

// The candidate pool can span weeks (it runs to 14 days past the end of a
// vacation), so the dropdown groups its options by day.
const groupSlotsByDay = (slots: AppointmentSlot[]) => {
  const groups: { label: string; slots: AppointmentSlot[] }[] = [];
  for (const slot of slots) {
    const label = fmtDayLabel(slot.start_time);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.slots.push(slot);
    else groups.push({ label, slots: [slot] });
  }
  return groups;
};

const BulkRescheduleScreen: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const source = (searchParams.get("source") as BulkRescheduleSource) || "sick_day";
  const doctorId = searchParams.get("doctor_id") || "";
  const date = searchParams.get("date") || undefined;
  const blockTimeId = searchParams.get("block_time_id") || undefined;

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<SuggestedSlotItem[]>([]);
  const [selections, setSelections] = useState<Record<string, string>>({}); // appointment_id -> new_slot_id
  const [reasonNote, setReasonNote] = useState(source === "doctor_block" ? "Doctor time blocked" : "Doctor called in sick");
  const [alternativeSlots, setAlternativeSlots] = useState<AppointmentSlot[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BulkRescheduleResponse | null>(null);

  useEffect(() => {
    if (!doctorId) { setLoading(false); return; }
    // The dropdown's options are the exact pool the suggestions were drawn
    // from (returned alongside them), not a separate fixed 14-days-from-today
    // fetch that couldn't reach past a long vacation.
    AppointmentAPI.getBulkRescheduleSuggestions({ source, doctor_id: doctorId, date, block_time_id: blockTimeId })
      .then(({ items: suggestions, candidate_slots }) => {
        setItems(suggestions);
        setAlternativeSlots(candidate_slots);
        const initial: Record<string, string> = {};
        for (const item of suggestions) {
          if (item.suggested_slot) initial[item.appointment_id] = item.suggested_slot.slot_id;
        }
        setSelections(initial);
      })
      .catch((e: any) => toast.error(e?.message || "Failed to load affected appointments"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId, source, date, blockTimeId]);

  const slotGroups = useMemo(() => groupSlotsByDay(alternativeSlots), [alternativeSlots]);
  const patientNameById = useMemo(
    () => Object.fromEntries(items.map((i) => [i.appointment_id, i.patient_name])),
    [items],
  );

  const unassignedCount = useMemo(
    () => items.filter((i) => !selections[i.appointment_id]).length,
    [items, selections]
  );

  // How many rows currently point at each slot — used to catch two patients
  // being assigned the same slot before it ever reaches the backend.
  const slotAssignmentCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const slotId of Object.values(selections)) {
      if (slotId) counts[slotId] = (counts[slotId] ?? 0) + 1;
    }
    return counts;
  }, [selections]);

  // A slot is over-assigned once more rows point at it than it has capacity
  // left (capacity is almost always 1, so in practice: picked twice = conflict).
  const overAssignedSlotIds = useMemo(() => {
    const overAssigned = new Set<string>();
    for (const slot of alternativeSlots) {
      const available = slot.capacity - slot.booked_count;
      if ((slotAssignmentCounts[slot.slot_id] ?? 0) > available) {
        overAssigned.add(slot.slot_id);
      }
    }
    return overAssigned;
  }, [alternativeSlots, slotAssignmentCounts]);

  const handleSubmit = async () => {
    const trimmedReason = reasonNote.trim();
    if (!trimmedReason) {
      toast.error("Add a reason for the reschedule");
      return;
    }
    const readyItems = items.filter((i) => selections[i.appointment_id]);
    if (readyItems.length === 0) {
      toast.error("No appointments have a slot assigned");
      return;
    }
    const conflicted = readyItems.filter((i) => overAssignedSlotIds.has(selections[i.appointment_id]));
    if (conflicted.length > 0) {
      toast.error(
        `Same slot assigned to more than one patient (${conflicted.map((i) => i.patient_name).join(", ")}) — pick a different slot for each before continuing`
      );
      return;
    }
    if (unassignedCount > 0 && !window.confirm(`${unassignedCount} appointment(s) have no slot assigned and will be skipped — continue?`)) {
      return;
    }
    setSubmitting(true);
    try {
      const response = await AppointmentAPI.applyBulkReschedule({
        source, doctor_id: doctorId, date, block_time_id: blockTimeId,
        reason_note: trimmedReason,
        items: readyItems.map((i) => ({ appointment_id: i.appointment_id, new_slot_id: selections[i.appointment_id] })),
        notify: true,
      });
      setResult(response);
      if (response.failed.length === 0) {
        toast.success(`${response.succeeded.length} appointment(s) rescheduled and patients notified`);
      }
    } catch (e: any) {
      toast.error(e.message || "Failed to apply bulk reschedule");
    } finally {
      setSubmitting(false);
    }
  };

  const pageTitle = source === "doctor_block" ? "Resolve Blocked-Time Conflicts" : "Doctor Called In Sick — Bulk Reschedule";

  if (!doctorId) {
    return (
      <div className="flex h-screen bg-[#f7f8fc] overflow-hidden">
        <Sidebar />
        <div className="flex-1 overflow-y-auto min-w-0 lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0">
          <AppHeader title={pageTitle} />
          <div className="p-4 sm:p-6">
            <p className="text-sm text-red-600">Missing doctor_id in the URL.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-[#f7f8fc] overflow-hidden">
      <Sidebar />
      <div className="flex-1 overflow-y-auto min-w-0 lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0">
        <AppHeader title={pageTitle} />
        <div className="p-4 sm:p-6">
      {loading ? (
        <p className="text-sm text-slate-400">Loading affected appointments…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400">No affected appointments found.</p>
      ) : result ? (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-3">
          <p className="text-sm text-slate-700">
            {result.succeeded.length} rescheduled and notified; {result.failed.length} failed.
          </p>
          {result.failed.length > 0 && (
            <ul className="text-sm text-red-600 list-disc pl-5">
              {result.failed.map((f) => (
                <li key={f.appointment_id}>{patientNameById[f.appointment_id] || f.appointment_id}: {f.error}</li>
              ))}
            </ul>
          )}
          <button
            onClick={() => navigate("/appointments")}
            className="bg-cyan-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-cyan-600 transition"
          >
            Back to Appointments
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4">
          <div>
            {/* Stored on each appointment's reschedule history and in the
                doctor's summary notification - the patient messages use the
                standard confirmation template, which doesn't carry it. */}
            <label htmlFor="bulk-reason" className="block text-xs font-medium text-slate-500 mb-1">
              Reason (saved to each appointment's reschedule history)
            </label>
            <input
              id="bulk-reason"
              value={reasonNote}
              onChange={(e) => setReasonNote(e.target.value)}
              maxLength={500}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-3">Patient</th>
                  <th className="py-2 pr-3">Current Time</th>
                  <th className="py-2 pr-3">New Slot</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.appointment_id} className="border-b border-slate-50">
                    <td className="py-2.5 pr-3 font-medium text-slate-800">{item.patient_name}</td>
                    <td className="py-2.5 pr-3 text-slate-600">{fmt(item.current_slot.start_time)}</td>
                    <td className="py-2.5 pr-3">
                      {(() => {
                        const selectedSlotId = selections[item.appointment_id] ?? "";
                        const isConflicted = selectedSlotId && overAssignedSlotIds.has(selectedSlotId);
                        return (
                          <>
                            <select
                              value={selectedSlotId}
                              onChange={(e) => setSelections((p) => ({ ...p, [item.appointment_id]: e.target.value }))}
                              className={`rounded-lg border px-2 py-1.5 text-sm ${
                                isConflicted
                                  ? "border-red-300 bg-red-50"
                                  : selectedSlotId ? "border-slate-200" : "border-amber-300 bg-amber-50"
                              }`}
                            >
                              <option value="">No slot assigned</option>
                              {slotGroups.map((group) => (
                                <optgroup key={group.label} label={group.label}>
                                  {group.slots.map((s) => (
                                    <option key={s.slot_id} value={s.slot_id}>{fmtTimeOnly(s.start_time)}</option>
                                  ))}
                                </optgroup>
                              ))}
                            </select>
                            {isConflicted && (
                              <span className="ml-2 text-xs text-red-600">
                                Also assigned to another patient — pick a different slot
                              </span>
                            )}
                            {!selectedSlotId && (
                              <span className="ml-2 text-xs text-amber-600">
                                {item.reason_no_suggestion ?? "No suggestion"}
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-cyan-500 text-white px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-cyan-600 disabled:opacity-40 transition"
          >
            {submitting ? "Rescheduling…" : "Reschedule & Notify All"}
          </button>
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

export default BulkRescheduleScreen;
